/**
 * The decisions behind the installed app, without a browser: whether to offer the install, and what
 * this device forgets when somebody signs out or switches workspace. Pure, so the tests say it.
 */

/** Where «Not now» is remembered: once dismissed, the hint is never shown again on this device. */
export const INSTALL_HINT_KEY = 'core.app.installHint';

export interface Place {
  /** Already running from the home screen (`display-mode: standalone`, or iOS's `navigator.standalone`). */
  standalone: boolean;
  /** A phone-sized screen: the hint is for employees on their phones, not for a desk. */
  phone: boolean;
  /** The person said «Not now», or installed it, on this device before. */
  dismissed: boolean;
  /** The browser handed over its install prompt (`beforeinstallprompt`: Chrome, Edge, Samsung Internet). */
  canPrompt: boolean;
  /** iOS or iPadOS, where an app is installed by hand from the Share menu and no prompt exists. */
  ios: boolean;
}

/** `prompt`: a button that opens the browser's own install dialog; `ios`: the two steps to take; null: nothing. */
export type InstallHint = 'prompt' | 'ios' | null;

export function installHintFor(p: Place): InstallHint {
  if (p.standalone || !p.phone || p.dismissed) return null;
  if (p.canPrompt) return 'prompt';
  if (p.ios) return 'ios';
  // A browser that neither prompts nor is iOS has its own menu entry, or none; a hint that cannot be
  // acted on is noise.
  return null;
}

/** iOS and iPadOS — iPadOS 13+ says it is a Mac, and gives itself away by having a touch screen. */
export function isIos(userAgent: string, platform: string, maxTouchPoints: number): boolean {
  return /iPhone|iPad|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

/**
 * What survives a sign-out or a workspace switch in `localStorage`: how this device shows the app,
 * never what anybody looked at. Everything else goes — recents and favourites (they name records),
 * and any key added later that nobody thought to list here, which is why this is a list of what
 * stays rather than of what goes. `sessionStorage` (the assistant's conversation, a list's order
 * for previous and next) is cleared whole.
 */
const KEPT_EXACT = new Set([
  'core.theme', 'core.locale', 'core.nav',
  'core.assistant.mode', 'core.assistant.box',
  'list.columns.all', 'list.board', 'list.view', 'calendar.mine.hidden', 'work.mine.hidden', 'record.cards.all',
  INSTALL_HINT_KEY,
]);
const KEPT_PREFIXES = [
  // A part of a hub somebody chose not to see, and the line of work the rail last held: display choices.
  'hub.hidden:', 'core.domain.',
  // A person's answer about session replay, asked once per person and workspace (analytics-model.ts).
  'core.analytics.replay.',
  // PostHog's own; `forgetViewer` (analytics.ts) already unlinks the next person from this one.
  'ph_',
];

export function keepsAcrossPeople(key: string): boolean {
  return KEPT_EXACT.has(key) || KEPT_PREFIXES.some((p) => key.startsWith(p));
}

export function keysToForget(keys: readonly string[]): string[] {
  return keys.filter((k) => !keepsAcrossPeople(k));
}

/** The caches the shell's worker owns (sw.js `CACHE_PREFIX`); anything else on the origin is not ours to delete. */
export const CACHE_PREFIX = 'core-shell-';
export function ownCaches(names: readonly string[]): string[] {
  return names.filter((n) => n.startsWith(CACHE_PREFIX));
}
